import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input, InputAmount, Tabs, TabPanel } from '@/backoffice/components/ui';
import {
  fetchDeliveryTypes,
  fetchPaymentMethods,
  createDeliveryType,
  deleteDeliveryType,
  createPaymentMethod,
  deletePaymentMethod,
} from '@/domains/admin/services/admin.service';
import { formatCurrency } from '@/backoffice/utils';
import { Plus, Trash2, Loader2, Wallet } from 'lucide-react';

export function SuperAdminPricingPage() {
  const [deliveryTypes, setDeliveryTypes] = useState<Array<{ id: number; name: string; amount_per_kg: number; description?: string }>>([]);
  const [paymentMethods, setPaymentMethods] = useState<Array<{ id: number; name: string; description?: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [newDelivery, setNewDelivery] = useState({ name: '', amount_per_kg: 500, description: '' });
  const [newPayment, setNewPayment] = useState({ name: '', description: '' });

  const load = () => {
    setLoading(true);
    Promise.all([fetchDeliveryTypes(), fetchPaymentMethods()])
      .then(([dt, pm]) => {
        setDeliveryTypes(dt);
        setPaymentMethods(pm);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <DashboardLayout userType="super_admin" title="Tarification">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { key: 'delivery', label: 'Types de livraison', badge: deliveryTypes.length },
    { key: 'payment', label: 'Modes de paiement', badge: paymentMethods.length },
  ];

  return (
    <DashboardLayout userType="super_admin" title="Tarification">
      <Tabs tabs={tabs}>
        <TabPanel>
          <div className="grid md:grid-cols-4 gap-3 mb-6">
            <Input placeholder="Nom" value={newDelivery.name} onChange={(e) => setNewDelivery((d) => ({ ...d, name: e.target.value }))} />
            <InputAmount label="" value={newDelivery.amount_per_kg} onChange={(v) => setNewDelivery((d) => ({ ...d, amount_per_kg: v }))} />
            <Input placeholder="Description" value={newDelivery.description} onChange={(e) => setNewDelivery((d) => ({ ...d, description: e.target.value }))} />
            <Button leftIcon={<Plus className="w-4 h-4" />} onClick={async () => {
              if (!newDelivery.name) return;
              await createDeliveryType(newDelivery);
              setNewDelivery({ name: '', amount_per_kg: 500, description: '' });
              load();
            }}>Ajouter</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {deliveryTypes.map((dt) => (
              <Card key={dt.id}>
                <div className="flex justify-between">
                  <div>
                    <p className="font-semibold">{dt.name}</p>
                    <p className="text-emerald-600 font-bold">{formatCurrency(dt.amount_per_kg)}/kg</p>
                    <p className="text-sm text-gray-500">{dt.description}</p>
                  </div>
                  <button type="button" onClick={async () => { await deleteDeliveryType(dt.id); load(); }} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </TabPanel>
        <TabPanel>
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <Input placeholder="Nom" value={newPayment.name} onChange={(e) => setNewPayment((p) => ({ ...p, name: e.target.value }))} />
            <Input placeholder="Description" value={newPayment.description} onChange={(e) => setNewPayment((p) => ({ ...p, description: e.target.value }))} />
            <Button leftIcon={<Plus className="w-4 h-4" />} onClick={async () => {
              if (!newPayment.name) return;
              await createPaymentMethod(newPayment);
              setNewPayment({ name: '', description: '' });
              load();
            }}>Ajouter</Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {paymentMethods.map((pm) => (
              <Card key={pm.id}>
                <div className="flex justify-between">
                  <div className="flex gap-3">
                    <Wallet className="w-5 h-5 text-violet-600" />
                    <div>
                      <p className="font-semibold">{pm.name}</p>
                      <p className="text-sm text-gray-500">{pm.description}</p>
                    </div>
                  </div>
                  <button type="button" onClick={async () => { await deletePaymentMethod(pm.id); load(); }} className="text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </Card>
            ))}
          </div>
        </TabPanel>
      </Tabs>
    </DashboardLayout>
  );
}
